import { createI18n } from "vue-i18n";
import fi from "./locales/fi.json";
import en from "./locales/en.json";

/**
 * vue-i18n setup.
 *
 * Composition-API mode (`legacy: false`) is the only supported
 * authoring style going forward — components call `useI18n()` and
 * read `t('a.b.c')` via the returned helper. We also enable
 * `globalInjection` so existing components that haven't been
 * migrated yet can still reach `$t` from templates without
 * wiring `useI18n()` first.
 *
 * Default locale is Finnish — the canonical strings live in
 * `locales/fi.json`. English (`en.json`) exists today as a stub,
 * so we point `fallbackLocale` at `fi`. Any English key whose
 * value is empty/missing will render the Finnish text instead of
 * the bare key — see `docs/I18N.md` for the migration recipe.
 *
 * The Pinia store `useI18nStore` (in `@/stores/i18n`) reads/writes
 * the user's preferred locale to localStorage and pushes changes
 * back into `i18n.global.locale.value`.
 */
export const SUPPORTED_LOCALES = ["fi", "en"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = "fi";

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: DEFAULT_LOCALE,
  fallbackLocale: "fi",
  messages: {
    fi,
    en,
  },
  // Vue-i18n logs a warning for every fallback hit which makes the
  // console unusable while English is still a stub. Silence those —
  // missing-key warnings still fire in dev, which is what we want.
  silentFallbackWarn: true,
  silentTranslationWarn: false,
});
